import React, { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  MapPin, Calendar, Clock, Building2, User, Phone, Mail, Briefcase,
  Plus, Trash2, X, FileText, Upload, Camera, Eye, ChevronDown, ChevronUp,
  Navigation, AlertCircle, CheckCircle2, IdCard, Image as ImageIcon, Pencil
} from 'lucide-react';

const DOC_TYPES = [
  { key: 'boq', label: 'BOQ', color: 'blue' },
  { key: 'drawings', label: 'Drawings', color: 'purple' },
  { key: 'specifications', label: 'Specifications', color: 'green' },
  { key: 'rfq', label: 'RFQ', color: 'orange' },
  { key: 'other', label: 'Other', color: 'gray' },
];

const PHOTO_SLOTS = [
  { key: 'business_card', label: 'Business Card / بطاقة العمل', required: true, urlField: 'business_card_url' },
  { key: 'project_facade', label: 'Project Facade / واجهة المشروع', required: false, urlField: 'project_facade_url' },
  { key: 'project_sign', label: 'Project Sign Board / لوحة المشروع', required: false, urlField: 'project_sign_url' },
];

const initialForm = {
  customer_id: '',
  customer_name: '',
  is_new_customer: false,
  project_name: '',
  city: '',
  site_address: '',
  contact_name: '',
  contact_title: '',
  contact_mobile: '',
  contact_email: '',
  materials_requested: '',
  notes: '',
};

const FieldVisits = ({ user }) => {
  const [visits, setVisits] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const [expandedVisit, setExpandedVisit] = useState(null);

  // GPS state
  const [gps, setGps] = useState(null);
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle | requesting | granted | denied | unsupported
  const [gpsError, setGpsError] = useState('');

  // Edit mode: visit id being edited, or null when creating new
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState(initialForm);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [visitsRes, weeklyRes] = await Promise.allSettled([
        axiosInstance.get('/field-visits'),
        axiosInstance.get('/field-visits/summary/weekly'),
      ]);
      if (visitsRes.status === 'fulfilled') setVisits(visitsRes.value.data);
      if (weeklyRes.status === 'fulfilled') setWeekly(weeklyRes.value.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---------- GPS capture (mandatory) ----------
  const captureGPS = () => {
    if (!('geolocation' in navigator)) {
      setGpsStatus('unsupported');
      setGpsError('Geolocation is not supported by this browser');
      return;
    }
    setGpsStatus('requesting');
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setGpsStatus('granted');
      },
      (err) => {
        setGpsStatus('denied');
        setGpsError(err.message || 'Location permission denied');
        toast.error('Location is required to save the visit');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const openModal = () => {
    setEditingId(null);
    setFormData(initialForm);
    setGps(null);
    setGpsStatus('idle');
    setGpsError('');
    setShowModal(true);
    // Auto-request GPS on modal open
    setTimeout(captureGPS, 300);
  };

  const openEditModal = (visit) => {
    setEditingId(visit.id);
    setFormData({
      customer_id: visit.customer_id || '',
      customer_name: visit.customer_name || '',
      is_new_customer: visit.is_new_customer || false,
      project_name: visit.project_name || '',
      city: visit.city || '',
      site_address: visit.site_address || '',
      contact_name: visit.contact_name || '',
      contact_title: visit.contact_title || '',
      contact_mobile: visit.contact_mobile || '',
      contact_email: visit.contact_email || '',
      materials_requested: visit.materials_requested || '',
      notes: visit.notes || '',
    });
    // For editing, no GPS is needed — preserve original
    setGps({ latitude: 0, longitude: 0, accuracy: 0, __edit: true });
    setGpsStatus('granted');
    setGpsError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialForm);
    setGps(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingId && !gps) {
      toast.error('GPS location is required. Please enable location.');
      return;
    }
    if (!formData.customer_name.trim()) {
      toast.error('Customer / Company name is required');
      return;
    }
    if (!formData.project_name.trim()) {
      toast.error('Project name is required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        // EDIT mode — PUT, no GPS change
        const updateBody = {
          customer_id: formData.customer_id || null,
          customer_name: formData.customer_name,
          is_new_customer: formData.is_new_customer,
          project_name: formData.project_name,
          city: formData.city,
          site_address: formData.site_address,
          contact_name: formData.contact_name,
          contact_title: formData.contact_title,
          contact_mobile: formData.contact_mobile,
          contact_email: formData.contact_email || null,
          materials_requested: formData.materials_requested,
          notes: formData.notes,
        };
        await axiosInstance.put(`/field-visits/${editingId}`, updateBody);
        toast.success('Visit updated!');
      } else {
        // CREATE mode — POST with GPS
        const payload = {
          ...formData,
          customer_id: formData.customer_id || null,
          contact_email: formData.contact_email || null,
          gps,
        };
        const res = await axiosInstance.post('/field-visits', payload);
        toast.success(`Visit ${res.data.visit_no} saved!`);
      }
      closeModal();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save visit');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Photo & file uploads (called from visit detail after creation) ----------
  const uploadPhoto = async (visitId, slot, file) => {
    const fd = new FormData();
    fd.append('slot', slot);
    fd.append('file', file);
    try {
      await axiosInstance.post(`/field-visits/${visitId}/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo uploaded');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const uploadAttachment = async (visitId, docType, file) => {
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);
    try {
      await axiosInstance.post(`/field-visits/${visitId}/upload-attachment`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const deleteVisit = async (id) => {
    if (!window.confirm('Delete this visit and all attached files?')) return;
    try {
      await axiosInstance.delete(`/field-visits/${id}`);
      toast.success('Visit deleted');
      fetchAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Group by day (newest first)
  const groupedByDay = visits.reduce((acc, v) => {
    const d = v.visit_date || '—';
    if (!acc[d]) acc[d] = [];
    acc[d].push(v);
    return acc;
  }, {});
  const sortedDays = Object.keys(groupedByDay).sort((a, b) => b.localeCompare(a));

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;

  return (
    <div data-testid="field-visits-page">
      <div className="page-header">
        <h2 className="page-title">Field Visits / الزيارات الميدانية</h2>
        <button
          data-testid="new-field-visit-btn"
          onClick={openModal}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" /> New Visit / زيارة جديدة
        </button>
      </div>

      {/* Weekly Summary */}
      {weekly.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-600 uppercase mb-3">Weekly Summary / ملخص أسبوعي</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {weekly.map((w) => (
              <div key={w.week_key} className="bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-400 transition-colors">
                <p className="text-[10px] text-gray-500">{w.start_date}</p>
                <p className="text-[10px] text-gray-500">→ {w.end_date}</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{w.count}</p>
                <p className="text-[11px] text-gray-500">visits</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily groups */}
      <div className="space-y-3">
        {sortedDays.length === 0 && (
          <div className="text-center py-16 text-gray-400 bg-white rounded-lg border-2 border-dashed border-gray-200">
            <MapPin className="w-12 h-12 mx-auto mb-3 opacity-40" />
            No visits recorded yet. Click "New Visit" to start.
          </div>
        )}
        {sortedDays.map((day) => {
          const list = groupedByDay[day];
          const isOpen = expandedDay === day;
          return (
            <div key={day} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                data-testid={`day-group-${day}`}
                onClick={() => setExpandedDay(isOpen ? null : day)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-gray-800">{day}</span>
                  <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                    {list.length} visit{list.length > 1 ? 's' : ''}
                  </span>
                </div>
                {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {isOpen && (
                <div className="border-t border-gray-100">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Time</th>
                        <th className="px-3 py-2 text-left">Project</th>
                        <th className="px-3 py-2 text-left">Customer</th>
                        <th className="px-3 py-2 text-left">Employee</th>
                        <th className="px-3 py-2 text-left">City</th>
                        <th className="px-3 py-2 text-center">Map</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {list.map((v) => (
                        <VisitRow
                          key={v.id}
                          visit={v}
                          expanded={expandedVisit === v.id}
                          onToggle={() => setExpandedVisit(expandedVisit === v.id ? null : v.id)}
                          onEdit={() => openEditModal(v)}
                          onUploadPhoto={(slot, file) => uploadPhoto(v.id, slot, file)}
                          onUploadAttachment={(docType, file) => uploadAttachment(v.id, docType, file)}
                          onDelete={() => deleteVisit(v.id)}
                          canDelete={user?.role === 'admin'}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* New Visit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold text-gray-900">
                {editingId ? 'Edit Field Visit / تعديل الزيارة' : 'New Field Visit'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" data-testid="close-modal-btn">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* GPS status block */}
              {/* GPS block only for NEW visits (edit preserves original location) */}
              {!editingId && (
                <GPSBlock gps={gps} status={gpsStatus} error={gpsError} onRetry={captureGPS} />
              )}
              {editingId && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                  <strong>Editing existing visit.</strong> Original GPS location is preserved.
                </div>
              )}

              {/* Section: Project Info */}
              <Section title="1. Project Information / معلومات المشروع" icon={Briefcase}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Customer / Company Name * / اسم العميل أو الشركة</Label>
                    <input
                      type="text"
                      required
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value, customer_id: '', is_new_customer: false })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors"
                      placeholder="Type the company name here..."
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <Label>Project Name *</Label>
                    <input type="text" required value={formData.project_name}
                      onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors" data-testid="project-name-input" />
                  </div>
                  <div>
                    <Label>City</Label>
                    <input type="text" value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Site Address</Label>
                    <input type="text" value={formData.site_address}
                      onChange={(e) => setFormData({ ...formData, site_address: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors" />
                  </div>
                </div>
              </Section>

              {/* Section: Contact Person */}
              <Section title="2. Contact Person / الشخص الذي قابلته" icon={User}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <input type="text" value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors" />
                  </div>
                  <div>
                    <Label>Job Title</Label>
                    <input type="text" value={formData.contact_title}
                      onChange={(e) => setFormData({ ...formData, contact_title: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors" />
                  </div>
                  <div>
                    <Label>Mobile</Label>
                    <input type="tel" value={formData.contact_mobile}
                      onChange={(e) => setFormData({ ...formData, contact_mobile: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors" placeholder="+966..." />
                  </div>
                  <div>
                    <Label>Email (optional)</Label>
                    <input type="email" value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors" />
                  </div>
                </div>
              </Section>

              {/* Section: Materials */}
              <Section title="3. Materials Requested / المواد المطلوبة" icon={FileText}>
                <textarea
                  rows={5}
                  value={formData.materials_requested}
                  onChange={(e) => setFormData({ ...formData, materials_requested: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors"
                  placeholder={'List one material per line, e.g.\n- Expansion Joint Cover, 100m\n- Fire Stop Sealant, 50 tubes\n- Rubber Protection'}
                  data-testid="materials-textarea"
                />
              </Section>

              {/* Section: Notes */}
              <Section title="4. Notes (optional)" icon={FileText}>
                <textarea
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors"
                />
              </Section>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                <strong>Note:</strong> Photos (business card, facade, sign board) and project files (BOQ, drawings…) can be added <strong>after saving</strong> from the visit's detail view.
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 sticky bottom-0 bg-white pt-4 border-t border-gray-200">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={(!editingId && !gps) || submitting}
                  data-testid="submit-visit-btn"
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Saving...' : editingId ? 'Update Visit' : gps ? 'Save Visit (Check-in)' : 'Waiting for location...'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== Sub-components ==============

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100">
      <Icon className="w-4 h-4 text-blue-600" /> {title}
    </h4>
    {children}
  </div>
);

const Label = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1">{children}</label>
);

const GPSBlock = ({ gps, status, error, onRetry }) => {
  const ok = status === 'granted' && gps;
  const denied = status === 'denied' || status === 'unsupported';
  return (
    <div className={`rounded-lg border-2 p-4 ${ok ? 'bg-green-50 border-green-300' : denied ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {ok ? (
            <CheckCircle2 className="w-6 h-6 text-green-600 mt-0.5" />
          ) : denied ? (
            <AlertCircle className="w-6 h-6 text-red-600 mt-0.5" />
          ) : (
            <Navigation className="w-6 h-6 text-yellow-600 mt-0.5 animate-pulse" />
          )}
          <div>
            <p className="font-semibold text-sm text-gray-900">
              {ok ? 'GPS Location Captured ✓' : denied ? 'Location Required' : 'Waiting for GPS...'}
            </p>
            {ok && (
              <div className="text-xs text-gray-700 mt-1 font-mono">
                <div>Lat: {gps.latitude.toFixed(6)}, Lng: {gps.longitude.toFixed(6)}</div>
                {gps.accuracy && <div>Accuracy: ±{Math.round(gps.accuracy)}m</div>}
              </div>
            )}
            {denied && <p className="text-xs text-red-700 mt-1">{error}. You cannot save a visit without enabling location.</p>}
            {!ok && !denied && <p className="text-xs text-yellow-700 mt-1">Allow location access in your browser.</p>}
          </div>
        </div>
        {!ok && (
          <button type="button" onClick={onRetry} className="text-xs px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50">
            Retry
          </button>
        )}
      </div>
    </div>
  );
};

const VisitRow = ({ visit, expanded, onToggle, onEdit, onUploadPhoto, onUploadAttachment, onDelete, canDelete }) => {
  return (
    <>
      <tr
        data-testid={`visit-row-${visit.id}`}
        onClick={onToggle}
        className="hover:bg-blue-50/40 cursor-pointer transition-colors"
      >
        <td className="px-3 py-2 font-mono text-xs text-gray-700 whitespace-nowrap">{visit.visit_no}</td>
        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{visit.visit_time}</td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">{visit.project_name}</span>
            {expanded ? <ChevronUp className="w-3 h-3 text-gray-400" /> : <ChevronDown className="w-3 h-3 text-gray-400" />}
          </div>
        </td>
        <td className="px-3 py-2 text-gray-700">
          {visit.customer_name}
          {visit.is_new_customer && (
            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">NEW</span>
          )}
        </td>
        <td className="px-3 py-2 text-gray-700">{visit.employee_name}</td>
        <td className="px-3 py-2 text-gray-600">{visit.city || '—'}</td>
        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
          {visit.gps ? (
            <a
              href={`https://www.google.com/maps?q=${visit.gps.latitude},${visit.gps.longitude}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-blue-100 text-blue-600"
              title="Open on Google Maps"
            >
              <MapPin className="w-4 h-4" />
            </a>
          ) : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <div className="inline-flex items-center gap-1">
            <button
              onClick={onEdit}
              className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
              title="Edit visit"
              data-testid={`edit-visit-${visit.id}`}
            >
              <Pencil className="w-4 h-4" />
            </button>
            {canDelete && (
              <button
                onClick={onDelete}
                className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                title="Delete visit"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50/40">
          <td colSpan={8} className="px-4 py-4 space-y-4">
            {/* Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <Detail icon={Calendar} label="Date / التاريخ" value={visit.visit_date} />
              <Detail icon={Clock} label="Time / الوقت" value={visit.visit_time} />
              <Detail icon={Building2} label="Customer" value={visit.customer_name} />
              <Detail icon={Briefcase} label="Project" value={visit.project_name} />
              <Detail icon={MapPin} label="City" value={visit.city || '—'} />
              <Detail icon={MapPin} label="Address" value={visit.site_address || '—'} />
              <Detail icon={User} label="Contact" value={visit.contact_name || '—'} />
              <Detail icon={Briefcase} label="Title" value={visit.contact_title || '—'} />
              <Detail icon={Phone} label="Mobile" value={visit.contact_mobile || '—'} />
              <Detail icon={Mail} label="Email" value={visit.contact_email || '—'} />
            </div>

            {/* Materials Requested */}
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1">Materials Requested / المواد المطلوبة:</p>
              {visit.materials_requested ? (
                <pre className="text-sm bg-white border border-gray-200 rounded p-3 whitespace-pre-wrap font-sans">{visit.materials_requested}</pre>
              ) : (
                <p className="text-sm text-gray-400 italic px-3 py-2">— No materials listed —</p>
              )}
            </div>

            {/* Notes */}
            <div data-testid="visit-notes-section">
              <p className="text-xs font-semibold text-gray-600 mb-1">Notes / ملاحظات:</p>
              {visit.notes ? (
                <pre data-testid="visit-notes-value" className="text-sm bg-white border border-gray-200 rounded p-3 whitespace-pre-wrap font-sans">{visit.notes}</pre>
              ) : (
                <p className="text-sm text-gray-400 italic px-3 py-2">— No notes —</p>
              )}
            </div>

            {/* Photos */}
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Photos:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PHOTO_SLOTS.map((slot) => (
                  <PhotoSlot
                    key={slot.key}
                    label={slot.label}
                    required={slot.required}
                    url={visit[slot.urlField]}
                    onPick={(file) => onUploadPhoto(slot.key, file)}
                  />
                ))}
              </div>
            </div>

            {/* Attachments */}
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Project Files:</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {DOC_TYPES.map((dt) => (
                  <AttachmentButton
                    key={dt.key}
                    doc={dt}
                    onPick={(file) => onUploadAttachment(dt.key, file)}
                  />
                ))}
              </div>
              {(visit.attachments || []).length > 0 && (
                <ul className="space-y-1">
                  {visit.attachments.map((att, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm bg-white border border-gray-200 rounded px-3 py-1.5">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-[10px] uppercase px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{att.doc_type}</span>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer"
                         className="text-blue-600 hover:underline flex-1 truncate">{att.name}</a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const Detail = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] uppercase text-gray-400 font-semibold">{label}</p>
      <p className="text-sm text-gray-800 truncate">{value}</p>
    </div>
  </div>
);

const PhotoSlot = ({ label, required, url, onPick }) => {
  const ref = useRef(null);
  return (
    <div className="bg-white border border-gray-200 rounded p-2">
      <p className="text-xs font-semibold text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </p>
      {url ? (
        <a href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={label}
               className="w-full h-32 object-cover rounded border border-gray-200 hover:opacity-90" />
        </a>
      ) : (
        <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400">
          <Camera className="w-6 h-6" />
        </div>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = '';
        }}
      />
      <button type="button" onClick={() => ref.current?.click()}
        className="mt-2 w-full text-xs py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-1">
        <Camera className="w-3 h-3" /> {url ? 'Retake' : 'Capture'}
      </button>
    </div>
  );
};

const AttachmentButton = ({ doc, onPick }) => {
  const ref = useRef(null);
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()}
        data-testid={`upload-${doc.key}-btn`}
        className="text-xs px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 hover:border-blue-400 flex items-center gap-1">
        <Upload className="w-3 h-3" /> {doc.label}
      </button>
      <input ref={ref} type="file"
        accept=".pdf,.dwg,.dxf,.xlsx,.xls,.docx,.doc,.zip,.rar,.png,.jpg,.jpeg"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = '';
        }}
      />
    </>
  );
};

export default FieldVisits;
